import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { SnackbarTypes } from 'src/app/core/_enums/snackbar.types';
import { PersonalAccessToken } from 'src/app/core/_models/personalAccessToken';
import { PersonalAccessTokenService } from 'src/app/core/_services/personal-access-token.service';
import { SnackbarService } from 'src/app/core/_services/snackbar.service';
import { environment } from 'src/environments/environment';
import { DialogCreatePersonalAccessTokenComponent } from '../shared/dialog-create-personal-access-token/dialog-create-personal-access-token.component';
import { DialogOverviewComponent } from '../shared/dialog-overview/dialog-overview.component';

@Component({
  selector: 'app-personal-access-token',
  templateUrl: './personal-access-token.component.html',
  styleUrls: ['./personal-access-token.component.scss']
})
export class PersonalAccessTokenComponent implements OnInit, OnChanges {
  @Input() userId?: string;

  tokens: PersonalAccessToken[] = [];
  contactMail = environment.adminMail;
  visible = false;

  private static readonly enabled = false;

  constructor(
    private personalAccessTokenService: PersonalAccessTokenService,
    private dialog: MatDialog,
    private snackbarService: SnackbarService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.load();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.userId && !changes.userId.firstChange) this.load();
  }

  load() {
    // Hidden for now: the keys are not to be created from the screen yet.
    if (!PersonalAccessTokenComponent.enabled) return;
    const tokens = this.userId ? this.personalAccessTokenService.getForUser(this.userId) : this.personalAccessTokenService.getMine();
    tokens.subscribe({
      next: (rep) => {
        this.tokens = rep ?? [];
        this.visible = true;
      },
      error: (error) => {
        // An administrator who does not manage this user gets a 403: the section stays hidden.
        this.visible = false;
        if (error?.status !== 403) this.snackbarService.open(SnackbarTypes.error);
      }
    });
  }

  // SEVE.API returns UTC dates without a time zone.
  utc(date?: string) {
    if (!date) return undefined;
    return /([zZ]|[+-]\d{2}:\d{2})$/.test(date) ? date : `${date}Z`;
  }

  status(token: PersonalAccessToken) {
    if (token.revokeDate) return 'revoked';
    const expireDate = this.utc(token.expireDate);
    if (expireDate && new Date(expireDate) <= new Date()) return 'expired';
    return 'active';
  }

  create() {
    const dialogRef = this.dialog.open(DialogCreatePersonalAccessTokenComponent, { disableClose: true });
    dialogRef.afterClosed().subscribe((created) => {
      if (created) this.load();
    });
  }

  revoke(token: PersonalAccessToken) {
    const dialogRef = this.dialog.open(DialogOverviewComponent, {
      data: { title: this.translate.instant('personal_access_token_confirm_revoke', { name: token.name }) }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (!result) return;

      const revoke = this.userId ? this.personalAccessTokenService.revokeForUser(this.userId, token.personalAccessTokenId) : this.personalAccessTokenService.revokeMine(token.personalAccessTokenId);
      revoke.subscribe({
        next: () => {
          this.snackbarService.showMessage(this.translate.instant('personal_access_token_revoked'));
          this.load();
        },
        error: () => this.snackbarService.open(SnackbarTypes.error)
      });
    });
  }
}
